import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTranslation } from 'react-i18next';

const EditGroup = () => {
  const { editGroup, getUserGroups, uploadGroupLogo, deleteGroupLogo, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [group, setGroup] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    isPublic: false,
    isSpecial: false
  });

  const fetchGroup = useCallback(async () => {
    try {
      setInitialLoading(true);
      const result = await getUserGroups();
      if (result.success) {
        const foundGroup = result.groups.find(g => g.id.toString() === id);
        if (foundGroup) {
          if (!foundGroup.isOwner) {
            setError(t('groups.notOwner') || 'You can only edit groups you created');
            return;
          }
          setGroup(foundGroup);
          setFormData({
            title: foundGroup.title,
            isPublic: foundGroup.isPublic,
            isSpecial: foundGroup.isSpecial || false
          });
        } else {
          setError(t('groups.groupNotFound') || 'Group not found');
        }
      } else {
        setError(result.error);
      }
    } catch (error) {
      setError('Failed to load group');
    } finally {
      setInitialLoading(false);
    }
  }, [id, getUserGroups, t]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEditGroup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    const result = await editGroup(id, formData.title, formData.isPublic, formData.isSpecial);
    
    if (result.success) {
      setSuccess(t('groups.editSuccess') || 'Group updated successfully!');
      
      // Navigate back to groups page after success
      setTimeout(() => {
        navigate('/groups');
      }, 2000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    navigate('/groups');
  };

  const handleFileChange = (e) => {
    setLogoFile(e.target.files[0]);
    setError('');
    setSuccess('');
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      setError(t('groups.noLogoSelected') || 'Please select a logo file');
      return;
    }

    setLoading(true);
    const result = await uploadGroupLogo(group.id, logoFile);
    
    if (result.success) {
      setSuccess(result.message);
      setLogoFile(null);
      // Update group with new logo
      setGroup(prev => ({ ...prev, logoUrl: result.logoUrl }));
      // Refresh group data
      setTimeout(() => fetchGroup(), 1000);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleLogoDelete = async () => {
    if (!group.logoUrl) return;

    setLoading(true);
    const result = await deleteGroupLogo(group.id);
    
    if (result.success) {
      setSuccess(result.message);
      // Update group to remove logo
      setGroup(prev => ({ ...prev, logoUrl: null }));
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (initialLoading) {
    return (
      <div className="container">
        <div className="loading">{t('common.loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (!group && !error) {
    return (
      <div className="container">
        <div className="error-message">{t('groups.groupNotFound') || 'Group not found'}</div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-card">
        <h2>{t('groups.editGroupTitle') || 'Edit Group'}</h2>
        
        {loading && <div className="loading">{t('common.loading') || 'Loading...'}</div>}
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {group && (
          <form onSubmit={handleEditGroup}>
            <div className="form-group">
              <label htmlFor="title">{t('groups.groupTitle') || 'Group Name'}:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder={t('groups.groupTitlePlaceholder') || 'Enter group name'}
                disabled={loading}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="isPublic">{t('groups.groupType') || 'Group Type'}:</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    id="isPublic"
                    name="isPublic"
                    checked={formData.isPublic}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                  {t('groups.makePublic') || 'Make this group public (others can discover and join)'}
                </label>
                <small className="form-hint">
                  {formData.isPublic 
                    ? (t('groups.publicHint') || 'Public groups can be discovered and joined by anyone')
                    : (t('groups.privateHint') || 'Private groups require an invitation code to join')
                  }
                </small>
              </div>
            </div>

            {/* Special Group Type for Admins */}
            {user?.isAdmin && (
              <div className="form-group">
                <label htmlFor="isSpecial">{t('groups.specialGroupType') || 'Special Group Type'}:</label>
                <div className="checkbox-group">
                  <label className="checkbox-label special-group-label">
                    <input
                      type="checkbox"
                      id="isSpecial"
                      name="isSpecial"
                      checked={formData.isSpecial}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                    {t('groups.makeSpecial') || 'Make this a special group (events can have logos and special styling)'}
                  </label>
                  <small className="form-hint special-hint">
                    {t('groups.specialHint') || 'Special groups allow events to have logo uploads and special dark green styling. Only admins can modify special group status.'}
                  </small>
                </div>
              </div>
            )}

            {/* Group Logo Upload for Special Groups */}
            {(group?.isSpecial || formData.isSpecial) && (
              <div className="form-group">
                <label>{t('groups.groupLogo') || 'Group Logo'}:</label>
                
                {/* Current Logo Display */}
                {group.logoUrl && (
                  <div className="current-logo-container">
                    <div className="logo-display">
                      <img 
                        src={group.logoUrl} 
                        alt="Group Logo" 
                        className="group-logo-image"
                      />
                    </div>
                    <div className="logo-actions">
                      <p className="delete-logo-text">{t('groups.deleteLogo') || 'Would you like to remove the current logo?'}</p>
                      <button 
                        type="button" 
                        onClick={handleLogoDelete}
                        className="btn btn-danger btn-sm"
                        disabled={loading}
                      >
                        {t('groups.deletelogo') || 'Delete Logo'}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Logo Upload */}
                <div className="logo-upload" style={{ marginTop: '10px' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={loading}
                    style={{ marginBottom: '10px' }}
                  />
                  {logoFile && (
                    <button 
                      type="button" 
                      onClick={handleLogoUpload}
                      className="btn btn-secondary btn-sm"
                      disabled={loading}
                    >
                      {loading ? (t('common.uploading') || 'Uploading...') : (t('groups.uploadLogo') || 'Upload Logo')}
                    </button>
                  )}
                </div>
                
                <small className="form-hint">
                  {t('groups.logoHint') || 'Special groups can have custom logos. Supported formats: JPG, PNG, GIF, SVG. Max size: 2MB.'}
                </small>
              </div>
            )}

            <div className="group-info-display">
              <h4>{t('groups.groupInfo') || 'Group Information'}</h4>
              <p><strong>{t('groups.groupCode') || 'Code'}:</strong> <span className="group-code">{group.code}</span></p>
              <p><strong>{t('groups.members') || 'Members'}:</strong> {group.memberCount}</p>
                              <p><strong>{t('groups.createdAt') || 'Created'}:</strong> {new Date(group.createdAt).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })}</p>
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
                {t('common.cancel') || 'Cancel'}
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (t('common.updating') || 'Updating...') : (t('groups.updateGroup') || 'Update Group')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default EditGroup; 